import { createSelector } from '@reduxjs/toolkit';
import { createCachedSelector } from 're-reselect';
import { get, values, find, isEmpty, isNil, map, compact, filter } from 'lodash/fp';
import { CustomPropertyTargets } from 'app/lib/constants';
import { selectEntityByPropId, selectSchemaForPropEntityId } from 'app/store/entities/selectors.js';
import { selectDatasetColumnFilterForEntityId } from 'app/store/columnFilter/selectors';
import { applySchemaFilter } from 'app/store/transformers.js';

const selectSlice = get('attributes');

const selectDefinitionsByAttributeId = createSelector(selectSlice, get('definitionsByAttributeId'));

export const selectCustomAttributes = createSelector(selectDefinitionsByAttributeId, values);

export const selectColumnAttributes = createSelector(
    selectCustomAttributes,
    filter({ target: CustomPropertyTargets.COLUMN })
);

export const selectDatasetAttributesCount = createSelector(
    selectCustomAttributes,
    (attributes) =>
        attributes.filter(
            (attr) => attr.target === CustomPropertyTargets.DATASET && !attr.prominent
        ).length
);

export const selectProjectAttributesCount = createSelector(
    selectCustomAttributes,
    (attributes) =>
        attributes.filter((attr) => attr.target === CustomPropertyTargets.SPACE && !attr.prominent)
            .length
);

const getProps = (state, props) => props;

export const selectAppearanceForAttributeId = createSelector(
    selectDefinitionsByAttributeId,
    getProps,
    (attributesById, props) => {
        const attribute = attributesById?.[props.attributeId];
        const option = find({ name: props.value }, attribute?.payload?.options);
        return option?.appearance;
    }
);

export const selectValuesForEntityId = createSelector(selectEntityByPropId, get('properties'));

export const selectShowDialog = createSelector(selectSlice, get('showDialog'));

export const selectError = createSelector(selectSlice, get('error'));

export const selectEntityAttributesAndValuesByPropId = createSelector(
    [selectValuesForEntityId, selectDefinitionsByAttributeId],
    (values, attributes) => {
        if (isNil(values) || isEmpty(attributes)) {
            return null;
        }

        return Object.values(values).reduce((acc, val) => {
            const attribute = attributes[val?.definitionUid || val?.uid];

            if (isNil(attribute)) {
                return acc;
            }

            const { name, prominent, uid, payload } = attribute;

            const value = {
                ...val.payload,
                uid,
                name,
                payload,
                prominent
            };

            if (prominent) {
                acc.prominent = [value, ...(acc?.prominent ?? [])];
            } else {
                acc.custom = [value, ...(acc?.custom ?? [])];
            }

            return acc;
        }, {});
    }
);

export const selectDatasetMetadataByPropId = createSelector(
    [selectSchemaForPropEntityId, selectColumnAttributes, selectDatasetColumnFilterForEntityId],
    (columns, columnAttributes, filteredColumns) => {
        if (isNil(columns) || isNil(columnAttributes)) {
            return null;
        }

        return map(
            (row) => {
                const customAttributeColumns = map(({ uid }) => {
                    const value = find((value) => value.definitionUid === uid, row.properties);
                    return value?.payload.value ?? null;
                }, columnAttributes);

                return [
                    row.columnUuid,
                    row.name,
                    row.typeFamily,
                    row.description ?? null,
                    ...customAttributeColumns
                ];
            },
            applySchemaFilter(columns, filteredColumns)
        );
    }
);

export const selectColumnAttributesAndValuesByPropId = createCachedSelector(
    selectEntityByPropId,
    selectCustomAttributes,
    (column, attributes) => {
        if (isNil(column) || isEmpty(attributes)) {
            return [];
        }

        return compact(
            map((value) => {
                const attribute = find({ uid: value.definitionUid }, attributes);

                // In very rare circumstances the attribute in question might be no longer available
                // This happend in concurrent runnng UATs
                if (isNil(attribute)) {
                    return null;
                }

                return {
                    name: attribute.name,
                    value: value.payload.value
                };
            }, column.properties)
        );
    }
)((_state_, props) => props.entityId);
